import CartIcon from 'assets/icons/base/shop-cart-icon.svg';
import { PaymentStatus } from 'constants/payment';
import { useAmount, useIsMobile, useTranslate } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { twMergeClsx } from 'utils';
import { AmountForm } from 'views/forms/base/amount-field';
import { DescriptionField } from 'views/forms/base/description-field';
import { PaymentBoxModal } from 'views/modals/base/payment-box-modal';

const CompanyInfo = () => {
  const { paymentData, acquiring } = useAppSelector((state) => state.payment);
  const { formattedAmount, commission, formattedCommission, formattedAmountWithCurrency } =
    useAmount();
  const { isMobile } = useIsMobile();
  const { t } = useTranslate();
  const isWaitPayment = paymentData?.status === PaymentStatus.WAIT_PAYMENT;
  const isCommentAllowed = Boolean(paymentData?.showCommentField);
  const canEditAmount = isWaitPayment && !paymentData?.fix;

  return (
    <div className="md:max-w-[345px] flex-grow w-full h-full md:py-4 md:px-[60px] flex flex-col md:justify-center gap-5 bg-white md:rounded-l-large relative">
      {(!isMobile || !acquiring) && (
        <div className="px-4 md:p-0 flex items-center justify-center md:items-start md:justify-normal md:flex-col gap-2 md:gap-6">
          <img src={CartIcon} className="w-8 md:w-12 h-8 md:h-12" alt="cart" />
          <h2 className="text-base md:text-xl font-bold leading-5 md:leading-6 text-black">
            {t('bin')}: {paymentData?.contragentName}
          </h2>
        </div>
      )}
      {isMobile ? (
        <>
          {!acquiring && (
            <div className="flex flex-col gap-3 px-4 md:p-0">
              <dl className="flex justify-between font-semibold items-center">
                <dt className="text-sm text-base-gray-800 leading-140">{t('lang.order_number')}</dt>
                <dd className="text-base leading-140">{paymentData?.ID}</dd>
              </dl>
              {canEditAmount ? (
                <dl className="flex justify-between font-semibold items-center flex-1">
                  <dt className="text-sm text-base-gray-800 w-full leading-140">
                    {t('amount')}, ₸ <span className="text-base-red-100 opacity-50">*</span>
                  </dt>
                  <dd className="text-base leading-140 w-full">
                    <AmountForm isMobile />
                  </dd>
                </dl>
              ) : (
                <dl className="flex justify-between font-semibold items-center flex-1">
                  <dt className="text-sm text-base-gray-800 w-full leading-140">
                    {t('lang.amount')}, ₸
                  </dt>
                  <dd className="text-base leading-140 w-full text-right">
                    {formattedAmountWithCurrency('')}
                  </dd>
                </dl>
              )}
              {isCommentAllowed && isWaitPayment && <DescriptionField isMobile />}
            </div>
          )}
          <div
            className={twMergeClsx(
              'md:hidden flex-col flex items-end p-4 border-t border-base-gray-500',
              acquiring ? 'flex-grow' : 'mt-auto',
            )}
          >
            <PaymentBoxModal />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4 md:gap-10">
          <dl className="font-bold md:font-semibold text-lg md:text-sm text-black leading-6 md:leading-5 text-center md:text-left">
            <dt>{t('lang.order_number')}</dt>
            <dd>{paymentData?.ID}</dd>
          </dl>
          <div className="flex flex-col gap-6 items-center text-center md:text-left md:items-start">
            {isWaitPayment && isCommentAllowed && <DescriptionField />}
            {canEditAmount ? (
              <>
                <AmountForm label={t('amount')} suffix=" ₸" />
                {isWaitPayment && Number(commission) > 0 && (
                  <dl>
                    <dt className="text-[10px] leading-3 mb-1 tracking-[2px] block uppercase font-nunito font-semibold text-base-dark-gray-200/60">
                      {t('commission')}
                    </dt>
                    <dd className="bg-[transparent] w-full outline-none font-bold text-2xl leading-7">
                      {formattedCommission}
                    </dd>
                  </dl>
                )}
              </>
            ) : (
              <>
                {paymentData?.description?.length && (
                  <dl>
                    <dt className="text-[10px] leading-3 mb-1 block uppercase tracking-[2px] font-nunito font-semibold text-base-dark-gray-200/60">
                      {t('kase.payment')}
                    </dt>
                    <dd className="bg-[transparent] w-full outline-none font-bold text-2xl leading-7">
                      {paymentData?.description}
                    </dd>
                  </dl>
                )}
                {isWaitPayment && (
                  <dl>
                    <dt className="text-[10px] leading-3 mb-1 block uppercase tracking-[2px] font-nunito font-semibold text-base-dark-gray-200/60">
                      {t('lang.amount')}
                    </dt>
                    <dd className="bg-[transparent] w-full outline-none font-bold text-2xl leading-7">
                      {formattedAmount}
                    </dd>
                  </dl>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { CompanyInfo };
